import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import {
  Person as PersonIcon,
  BookOnline as BookOnlineIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { amenityAPI, userAPI } from '../../services/api';
import { Amenity, User, UserRole } from '../../types';

interface ReserveAmenityDialogProps {
  open: boolean;
  onClose: () => void;
  amenity: Amenity | null;
  onSuccess: (message?: string) => void;
  preselectedDate?: Date | null;
  preselectedSlot?: { startTime: string; endTime: string } | null;
}

const ReserveAmenityDialog: React.FC<ReserveAmenityDialogProps> = ({
  open,
  onClose,
  amenity,
  onSuccess,
  preselectedDate,
  preselectedSlot
}) => {
  const [coachesAndEmployees, setCoachesAndEmployees] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [bookingDate, setBookingDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch coaches and employees when dialog opens
  useEffect(() => {
    if (open) {
      fetchCoachesAndEmployees();
      // Reset form state or populate from preselected values
      setSelectedUser(null);
      setBookingDate(preselectedDate || null);
      
      // Parse and set times from preselectedSlot if provided
      if (preselectedSlot) {
        const parseTime = (timeString: string): Date => {
          const [hours, minutes] = timeString.substring(0, 5).split(':').map(Number);
          const date = new Date();
          date.setHours(hours, minutes, 0, 0);
          return date;
        };
        
        setStartTime(parseTime(preselectedSlot.startTime));
        setEndTime(parseTime(preselectedSlot.endTime));
      } else {
        setStartTime(null);
        setEndTime(null);
      }
      
      setNotes('');
      setError(null);
      setSuccess(null);
    }
  }, [open, preselectedDate, preselectedSlot]);

  const fetchCoachesAndEmployees = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getUsers({ 
        roles: [UserRole.COACH, UserRole.EMPLOYEE, UserRole.ADMIN, UserRole.SUPER_ADMIN]
      });
      
      // Filter users with specific roles
      const filteredUsers = response.data.filter((user: User) => 
        [UserRole.COACH, UserRole.EMPLOYEE, UserRole.ADMIN, UserRole.SUPER_ADMIN].includes(user.role)
      );
      
      setCoachesAndEmployees(filteredUsers);
    } catch (error: any) {
      console.error('Error fetching coaches and employees:', error);
      setError('Failed to load coaches and employees');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const validateTimeIncrement = (time: Date): string | null => {
    const minutes = time.getMinutes();
    if (minutes % 15 !== 0) {
      return 'Times must be in 15-minute increments (e.g., 9:00, 9:15, 9:30, 9:45)';
    }

    return null;
  };

  const formatDateForAPI = (date: Date): string => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeForAPI = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleReserveAmenity = async () => {
    if (!selectedUser || !amenity || !bookingDate || !startTime || !endTime) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate time increments
    const startTimeError = validateTimeIncrement(startTime);
    const endTimeError = validateTimeIncrement(endTime);
    
    if (startTimeError || endTimeError) {
      setError(startTimeError || endTimeError);
      return;
    }

    // Validate that end time is after start time
    if (endTime <= startTime) {
      setError('End time must be after start time');
      return;
    }

    // Validate that booking is for future date or today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedBookingDate = new Date(bookingDate);
    selectedBookingDate.setHours(0, 0, 0, 0);
    
    if (selectedBookingDate < today) {
      setError('Cannot reserve amenities for past dates');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const reservationData = {
        bookingDate: formatDateForAPI(bookingDate),
        startTime: formatTimeForAPI(startTime),
        endTime: formatTimeForAPI(endTime),
        notes: notes.trim(),
        reservedForUserId: selectedUser.id,
        reservedForUserName: `${selectedUser.firstName} ${selectedUser.lastName}`,
        reservedForUserEmail: selectedUser.email,
        status: 'on_hold' // Special status for reservations
      };

      // Create a reservation booking
      await amenityAPI.createReservation(amenity.id, reservationData);
      
      setSuccess('Amenity reserved successfully!');
      setTimeout(() => {
        onSuccess('Amenity reserved successfully');
        handleClose();
      }, 1500);
    } catch (error: any) {
      console.error('Error reserving amenity:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to reserve amenity. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    return new Date(); // Today
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6); // 6 months in advance
    return maxDate;
  };

  const formatUserOption = (user: User) => {
    const roleLabel = user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `${user.firstName} ${user.lastName} (${roleLabel})`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={submitting}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BookOnlineIcon sx={{ mr: 1, color: 'primary.main' }} />
            Reserve Amenity
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {amenity && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Reserving:</strong> {amenity.name}
                  {amenity.location && (
                    <span> - {amenity.location}</span>
                  )}
                </Typography>
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Coach/Employee Selection */}
              <Grid size={12}>
                <Autocomplete
                  options={coachesAndEmployees}
                  getOptionLabel={(user) => formatUserOption(user)}
                  value={selectedUser}
                  onChange={(event, newValue) => setSelectedUser(newValue)}
                  loading={loading}
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Coach/Employee"
                      required
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      helperText="Select the coach or employee for whom this amenity is being reserved"
                    />
                  )}
                />
              </Grid>

              {/* Reservation Date */}
              <Grid size={12}>
                <DatePicker
                  label="Reservation Date"
                  value={bookingDate}
                  onChange={setBookingDate}
                  minDate={getMinDate()}
                  maxDate={getMaxDate()}
                  disabled={submitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }
                    }
                  }}
                />
              </Grid>

              {/* Start Time */}
              <Grid size={6}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  minutesStep={15}
                  disabled={submitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }
                    }
                  }}
                />
              </Grid>

              {/* End Time */}
              <Grid size={6}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  minutesStep={15}
                  disabled={submitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }
                    }
                  }}
                />
              </Grid>

              {/* Notes */}
              <Grid size={12}>
                <TextField
                  label="Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  disabled={submitting}
                  placeholder="Add any special notes or requirements for this reservation..."
                />
              </Grid>
            </Grid>

            {/* Reservation Summary */}
            {selectedUser && bookingDate && startTime && endTime && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Reservation Summary:
                </Typography>
                <Typography variant="body2">
                  <strong>For:</strong> {formatUserOption(selectedUser)}
                </Typography>
                <Typography variant="body2">
                  <strong>Amenity:</strong> {amenity?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {bookingDate.toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {formatTimeForAPI(startTime)} - {formatTimeForAPI(endTime)}
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))} minutes
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> On Hold (Reserved)
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleReserveAmenity}
            variant="contained"
            disabled={!selectedUser || !bookingDate || !startTime || !endTime || submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <BookOnlineIcon />}
          >
            {submitting ? 'Reserving...' : 'Reserve'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ReserveAmenityDialog;